import { EventEmitter } from 'events';
import { IFluidObject } from '@fluidframework/core-interfaces';
import {
  InsertComponentRequestData,
  ITelemetryNamespaceLogger,
  IContainerServiceFactory,
  DataRequestProcessor,
  DataRequestCreator,
  DataRequestCallback,
  MenuViewProvider,
  Audience
} from '@fluidx/office-fluid-types';
import { IContainerRuntime } from '@fluidframework/container-runtime-definitions';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { NamespaceLoggerAdapter, resolveLocale, Locale } from '@fluidx/utilities';
import { IFluidDependencySynthesizer } from '@fluidframework/synthesize';

/**
 * DiscoverComponent is the component that manages Discover, the one-stop shop for discovering and
 * executing component commands and inserting components from different available registries.
 *
 * For other components to participate in the menu results, they are expected to be processors of
 * DiscoverData. To invoke the component, other components can act as producers of InsertComponentRequestData and
 * allow Discover to invoke itself on some trigger (a keypress, button click, etc.).
 */
export class DiscoverComponent extends EventEmitter implements DataRequestProcessor, DataRequestCreator {
  public readonly supportedDataTypes: (keyof IFluidObject)[] = ['ComponentProduceInsertComponentRequestData'];
  private logger: ITelemetryBaseLogger | undefined;
  private producerCallback: DataRequestCallback | undefined;

  private menuViewProvider: Promise<MenuViewProvider | undefined>;
  private audience: Audience | undefined;
  private locale: Locale | undefined;

  public get DataRequestProcessor() {
    return this;
  }

  public get DataRequestCreator() {
    return this;
  }

  public registerDataRequestCallback(producerCallback: DataRequestCallback): void {
    this.producerCallback = producerCallback;
  }

  /**
   * @param loggerFactory is an optional parameter to allow Discover to be a service that can be
   * instantiated without a container logger.
   */
  constructor(
    loggerPromise?: Promise<ITelemetryNamespaceLogger | undefined>,
    menuViewProviderPromise?: Promise<MenuViewProvider | undefined>,
    audience?: Audience,
    locale?: string
  ) {
    super();

    if (loggerPromise) {
      // tslint:disable-next-line: no-floating-promises
      loggerPromise.then((resolvedLogger) => {
        if (resolvedLogger) {
          this.logger = new NamespaceLoggerAdapter('Discover', resolvedLogger);
        }
      });
    }

    this.menuViewProvider = menuViewProviderPromise || Promise.resolve(undefined);

    this.audience = audience;
    this.locale = resolveLocale(locale);
  }

  public static serviceFactory: IContainerServiceFactory = {
    get IContainerServiceFactory() {
      return this;
    },
    instantiateContainerService: (runtime: IContainerRuntime) => {
      let loggerPromise: Promise<ITelemetryNamespaceLogger | undefined> = runtime
        .request({ url: '/_services/NamespaceLogger' })
        .then((namespaceLoggerRequest) => {
          if (namespaceLoggerRequest.status === 200 && namespaceLoggerRequest.mimeType === 'fluid/object') {
            let namespaceLogger = (namespaceLoggerRequest.value as IFluidObject).NamespaceLogger;

            return Promise.resolve(namespaceLogger);
          }
          return Promise.reject();
        })
        .catch(() => {
          // if we don't have a namespace logger, we just create Discover with an undefined logger
          return undefined;
        });

      const dependencySynthesizer: IFluidDependencySynthesizer | undefined = runtime.scope.IFluidDependencySynthesizer;
      const menuViewProvider = dependencySynthesizer?.getProvider('MenuViewProvider');
      const menuViewProviderPromise =
        menuViewProvider instanceof Function ? undefined : Promise.resolve(menuViewProvider);

      return new DiscoverComponent(
        loggerPromise,
        menuViewProviderPromise,
        runtime.scope.HostContext?.audience,
        resolveLocale(runtime.scope.HostContext?.hostLocaleContext?.uiLanguage)
      );
    }
  };

  public processDataRequest(interfaceName: keyof IFluidObject, data: any): void {
    if (interfaceName === 'ComponentProduceInsertComponentRequestData') {
      this.handleInsertComponentRequestData(data);
    }
  }

  private handleInsertComponentRequestData(insertRequestData: InsertComponentRequestData): void {
    // TODO: Maybe we should filter Discover items based on InsertComponentRequestData requestedDataType.
    const producerCallback = this.producerCallback;

    if (producerCallback !== undefined) {
      // tslint:disable-next-line: no-floating-promises
      import(/* webpackChunkName: "renderDiscoverMenu", webpackPrefetch: true */ '../view/renderDiscoverMenu').then(
        async (m) => {
          const menuViewProvider = await this.menuViewProvider;
          await m.renderDiscoverMenu({
            insertRequestData,
            producerCallback,
            logger: this.logger,
            audience: this.audience,
            menuViewProvider,
            locale: this.locale
          });
        }
      );
    }
  }
}

export const serviceFactory = DiscoverComponent.serviceFactory;
